import React, { useEffect, useState, useMemo, createRef, useRef } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '@src/languages';
import { sortBy } from 'lodash';
import {
  DialogActions,
  DialogContent,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
} from '@mui/material';

import api from '@src/apis';
import actions from '@src/redux/actions';
import { OLD_VERSION_DUBBING_VOICE_CODES } from '@src/constants/voice';
import {
  MOBILE_BREAKPOINT,
  SMS_IDENTITY_PROVIDER,
  VIETNAM_LOCATION,
  LANGUAGE,
} from '@src/constants';
import { PLAYING_AUDIO_TYPE, SILENCE_AUDIO_URL } from '@src/constants/tts';
import VoiceNotFound from '@src/assets/icons/voice-not-found.svg';
import FEATURE_FLAG from '@src/constants/featureFlags.json';
import Feature from '@src/components/Feature';
import 'react-loading-skeleton/dist/skeleton.css';
import DialogNew from '@src/components/DialogNew';
import BadgeAvatar from '@src/components/BadgeAvatar';

import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import VoiceItem from './VoiceItem';
import { StyledImg, StyledTableScrollbar, StyledVoices } from './index.style';
import VoiceDetail from './VoiceDetail';
import VoiceSkeleton from './VoiceSkeleton';
import VoiceFilterAction from './VoiceFilterAction';
import NoData from '../NoData';
import VoicesFilter from './VoicesFilter';

const initFilterOpens = {
  gender: true,
  features: true,
  level: true,
  languageCode: true,
  voiceOwnership: true,
};

const VoicesDialogNew = ({
  open,
  activeVoiceId,
  defaultFilter = {},
  usingPackageType,
  hasLanguageFilter,
  onClose,
  onChangeVoice,
  oldDubbingVoices,
}) => {
  const isDubbingRoute = useCheckDubbingRoute();
  const { getFeatureValue } = useFeatureFlags();
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState({});
  const [openSort, setOpenSort] = useState(false);
  const [openFilter, setOpenFilter] = useState(
    window.innerWidth > MOBILE_BREAKPOINT,
  );
  const [loading, setLoading] = useState(false);
  const [audioLink, setAudioLink] = useState('');
  const [search, setSearch] = useState('');
  const [filterOpens, setFilterOpens] = useState(initFilterOpens);
  const [audioType, setAudioType] = useState(PLAYING_AUDIO_TYPE.SILENCE);
  const country = localStorage.getItem('country');
  const { filterVoice } = useSelector((state) => state.voice);
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();

  const isSMSUser = Object.values(SMS_IDENTITY_PROVIDER).includes(
    user.identityProvider,
  );
  const [showScrollbar, setShowScrollbar] = useState(false);
  const { t } = useTranslation();
  const audioRef = createRef(null);
  const dispatch = useDispatch();
  const [savedCurrentTime, setSavedCurrentTime] = useState(0);
  const [language, setLanguage] = useState(LANGUAGE.VN);
  const { language: currLang } = i18n;
  const [location, setLocation] = useState({});
  const { languages } = useSelector((state) => state.language);
  const voiceContainerRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const sortLanguages = useMemo(
    () =>
      languages?.sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;

        if (currLang === LANGUAGE.VN)
          return a.label?.vi?.localeCompare(b.label?.vi);
        return a.label?.en?.localeCompare(b.label?.en);
      }),
    [languages, currLang],
  );

  const languageOptions = sortLanguages?.map((item) => ({
    ...item,
    label: item.label[language] || item.label[LANGUAGE.EN],
  }));

  const defaultLanguageFilter = languageOptions?.find((item) =>
    item?.value.includes(location?.country),
  );

  const ATOZ = 'ATOZ';
  const ZTOA = 'ZTOA';
  const RECOMMEND = 'RECOMMEND';
  const optionsFilter = {
    [RECOMMEND]: t('recommend'),
    [ATOZ]: t('fromNameAtoZ'),
    [ZTOA]: t('fromNameZtoA'),
  };

  const [sort, setSort] = useState(RECOMMEND);

  const allowFilterByVoiceLibrary =
    getFeatureValue(FEATURE_KEYS.VOICE_CLONING, {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
      screenWidth: window.innerWidth,
    }) || validateUserPackageCanUseVoiceCloning(ttsUser);

  const canUseVoiceCloning = useMemo(
    () =>
      ttsUser?.packageExpiryDate &&
      moment().isBefore(ttsUser?.packageExpiryDate) &&
      allowFilterByVoiceLibrary,
    [ttsUser?.packageExpiryDate, allowFilterByVoiceLibrary],
  );

  const handleChangeFilterOpens = (name) => {
    const newFilterOpen = { ...filterOpens, [name]: !filterOpens[name] };
    setFilterOpens(newFilterOpen);
  };

  const fetchVoices = async () => {
    setLoading(true);
    const gender = filterVoice?.gender?.length ? filterVoice.gender : '';
    const languageCode = filterVoice?.languageCode?.length
      ? filterVoice.languageCode
      : defaultLanguageFilter.value;
    const features = filterVoice?.features?.length
      ? filterVoice.features.map((item) => item).join(',')
      : '';
    const level = filterVoice?.level?.length
      ? filterVoice.level.map((item) => item).join(',')
      : '';
    const voiceOwnership = filterVoice?.voiceOwnership;
    let data = {};

    if (!isDubbingRoute)
      data = await api.voices.getVoicesV2({
        search: search.trim(),
        gender,
        languageCode: isSMSUser ? 'vi-VN' : languageCode, // default get only Vietnamese voices for SMS user
        level,
        features,
        active: true,
        voiceOwnership,
        ...defaultFilter,
      });
    else
      data = await api.voices.getVoices({
        search: search.trim(),
        gender,
        languageCode: isSMSUser ? 'vi-VN' : languageCode, // default get only Vietnamese voices for SMS user
        level,
        features,
        active: true,
        ...defaultFilter,
      });

    setLoading(false);
    if (data.result) {
      const { hasDubbing } = defaultFilter;
      if (hasDubbing && oldDubbingVoices) {
        const oldVoices = data.result.voices.filter((voice) =>
          OLD_VERSION_DUBBING_VOICE_CODES.includes(voice.code),
        );
        setVoices(oldVoices);
        setSelectedVoice(
          data.result.voices.find((voice) => voice.id === activeVoiceId),
        );
      } else {
        setVoices(
          data.result.voices.map((voice) => ({ ...voice, isPlaying: false })),
        );
        setSelectedVoice(
          data.result.voices.find((voice) => voice.id === activeVoiceId),
        );
      }
    }
  };

  const handleAudioEnd = () => {
    if (audioType === PLAYING_AUDIO_TYPE.BACKGROUND_MUSIC) {
      setAudioType(PLAYING_AUDIO_TYPE.SILENCE);
      audioRef.current.currentTime = 0;
      audioRef.current.pause();
      const newVoices = voices.map((item) =>
        item.playStatus ? { ...item, playStatus: false } : item,
      );
      setVoices(newVoices);
      return;
    }

    if (audioLink) {
      setAudioType(PLAYING_AUDIO_TYPE.BACKGROUND_MUSIC);
      audioRef.current.src = audioLink;
      audioRef.current.load();
      audioRef.current.play();
      return;
    }

    audioRef.current.src = SILENCE_AUDIO_URL;
    audioRef.current.load();
    audioRef.current.play();
  };

  const handleClose = () => {
    setOpenFilter(window.innerWidth > MOBILE_BREAKPOINT);
    setOpenSort(false);
    setFilterOpens(initFilterOpens);
    setAudioLink('');
    handleAudioEnd();
    setSearch('');
    onClose();
  };
  const handleChangeFilter = (value) => {
    dispatch(actions.voice.changeFilterVoice(value));
  };

  const handleChangePlayAudio = (voice) => {
    dispatch(actions.audioPlayer.updateStatus(false));
    const audio = audioRef.current;

    if (!audioLink || voice.demo !== audioLink) {
      setAudioType(PLAYING_AUDIO_TYPE.SILENCE);
      audio.src = SILENCE_AUDIO_URL;
      audio.load();
      audio.play();
      setSavedCurrentTime(0);
      setAudioLink(voice.demo);
      audio.load();
    }

    const newVoices = voices.map((item) =>
      item.id === voice.id
        ? {
            ...voice,
            isPlaying: !voice.isPlaying,
            playStatus: !voice.playStatus,
          }
        : { ...item, isPlaying: false, playStatus: false },
    );
    setVoices(newVoices);
  };

  const handleSelectedVoice = (voice) => {
    onChangeVoice(voice);
    handleClose();
  };
  const handleReset = () => {
    setSearch('');
    dispatch(actions.voice.resetFilterVoice());
    setSort(RECOMMEND);
  };

  useEffect(() => {
    dispatch(actions.voice.resetFilterVoice(user.packageCode));
  }, [country]);
  useEffect(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setAudioLink('');
    }
  }, [search, filterVoice]);

  useEffect(() => {
    if (open && defaultLanguageFilter?.value) fetchVoices();
  }, [open, filterVoice, defaultLanguageFilter?.value]);

  useEffect(() => {
    if (i18n?.language === LANGUAGE.VN) setLanguage(LANGUAGE.VN);
    else setLanguage(LANGUAGE.EN);
  }, [i18n?.language]);

  const fetchCountry = async () => {
    try {
      const data = await api.country.getCountry();
      if (!data?.country) {
        setLocation(VIETNAM_LOCATION);
        return;
      }
      setLocation(data);
    } catch (error) {
      setLocation(VIETNAM_LOCATION);
    }
  };

  useEffect(() => {
    fetchCountry();
  }, []);

  useEffect(() => {
    setLoading(true);
  }, [location]);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    if (
      !audioRef.current?.paused &&
      voiceContainerRef?.current?.scrollTop > 0
    ) {
      voiceContainerRef.current.scrollTop = 0;
    }
  }, [voices]);

  const voicesView = useMemo(() => {
    if (sort === RECOMMEND) return voices;

    if (sort === ATOZ) {
      return sortBy(voices, (item) => item?.name);
    }
    if (sort === ZTOA) {
      return sortBy(voices, (item) => item?.name).reverse();
    }
    return voices;
  }, [voices, sort]);

  return (
    <DialogNew
      maxWidth="100%"
      onSc
      title={t('libraryVoices')}
      open={open}
      fullWidth
      onClose={handleClose}
    >
      <StyledVoices openSearch={openSort || openFilter}>
        <DialogContent className="content">
          <div className="voice-content">
            <VoiceFilterAction
              setSearch={setSearch}
              filter={filterVoice}
              hasLanguageFilter={hasLanguageFilter}
              onChangeFilter={handleChangeFilter}
              onHandleReset={handleReset}
              search={search}
              sort={sort}
              onSort={setSort}
              optionsFilter={optionsFilter}
            />

            <StyledTableScrollbar
              onMouseEnter={() => setShowScrollbar(true)}
              onMouseLeave={() => setShowScrollbar(false)}
            >
              <div
                ref={voiceContainerRef}
                className={`voice-container ${
                  showScrollbar ? 'small-scrollbar' : 'hidden-scrollbar'
                } `}
              >
                {voices.length > 0 && (
                  <Typography variant="title1" className="sticky-title">
                    {t('allVoices')}
                  </Typography>
                )}
                <div
                  className={`voice-list ${
                    loading || voices.length > 0 ? 'with-grid' : ''
                  }`}
                >
                  {loading ? (
                    <VoiceSkeleton voices={12} />
                  ) : (
                    <>
                      {voices.length > 0 ? (
                        <>
                          {voicesView.map((voiceItem) => (
                            <VoiceItem
                              key={voiceItem.id}
                              voice={voiceItem}
                              isPlaying={voiceItem.isPlaying}
                              activeVoiceId={activeVoiceId}
                              canUseVoiceCloning={canUseVoiceCloning}
                              usingPackageType={usingPackageType}
                              onChangePlayAudio={handleChangePlayAudio}
                              onSelectedVoice={handleSelectedVoice}
                              audioRef={audioRef}
                              setAudioType={setAudioType}
                              savedCurrentTime={savedCurrentTime}
                              setSavedCurrentTime={setSavedCurrentTime}
                            />
                          ))}
                        </>
                      ) : (
                        <div className="not-found-voice">
                          <StyledImg>
                            <NoData>
                              <img
                                style={{ height: '86px' }}
                                src={VoiceNotFound}
                                alt="no data"
                              />
                            </NoData>
                          </StyledImg>
                          <p className="text-not-found">
                            {t('noResultsWereFoundMatchingYourSearch')}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </StyledTableScrollbar>
          </div>
          <div className="web">
            <VoicesFilter
              filter={filterVoice}
              filterOpens={filterOpens}
              hasLanguageFilter={hasLanguageFilter}
              onChangeFilter={handleChangeFilter}
              onChangeFilterOpens={handleChangeFilterOpens}
              onHandleReset={handleReset}
              defaultLanguageFilter={defaultLanguageFilter}
              languageOptions={languageOptions}
            />
          </div>

          <Feature name={FEATURE_FLAG.NEW_VOICE_DIALOG}>
            <div className="voice-detail">
              {!loading && (
                <div>
                  <VoiceDetail selectedVoice={selectedVoice} />
                </div>
              )}
            </div>
          </Feature>
        </DialogContent>
        <Feature name={FEATURE_FLAG.NEW_VOICE_DIALOG}>
          <DialogActions classes={{ root: 'dialog-action-footer' }}>
            <div className="voice-active">
              <Typography variant="subtitle2">
                {t('usingVoice')} :&ensp;
              </Typography>
              <BadgeAvatar
                img={selectedVoice?.roundImage}
                smallImg={
                  selectedVoice &&
                  selectedVoice?.language &&
                  selectedVoice?.language?.roundImage
                }
                width={25}
                type="image"
              />
              <Typography variant="subtitle2">
                &ensp;{selectedVoice?.name}
              </Typography>
            </div>
            <div className="voice-action">
              <FormControlLabel
                control={<Checkbox />}
                labelPlacement="end"
                label={t('setDefault')}
                checked="true"
                className="label-checkbox"
              />
              <Button
                color="primary"
                variant="contained"
                className="btn-choose-voice"
              >
                {t('confirm')}
              </Button>
            </div>
          </DialogActions>
        </Feature>
      </StyledVoices>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} onEnded={handleAudioEnd}>
        <source src={audioLink} type="audio/wav" />
      </audio>
    </DialogNew>
  );
};

export default VoicesDialogNew;
